import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { logInWithProvider } from '../../adapters/ServiceAPI';

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
}

// Google "G" logo SVG - Official Google branding
const GoogleIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
    />
  </svg>
);

// Apple logo SVG - Official Apple branding
const AppleIcon = () => (
  <img src="/assets/image/Apple_Logo.svg" alt="Apple Icon" />
);

// Discord logo SVG - Official Discord branding
const DiscordIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 71 55"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3## 44.2785 53.4804 44.2898 53.5475 44.3433C53.9029 44.6363 54.2751 44.9293 54.6501 45.2082C54.7788 45.304 54.7## 45.5041 54.6277 45.5858C52.859 46.6197 51.0203 47.4931 49.0893 48.2228C48.9634 48.2707 48.9074 48.4172 48.969 48.5383C50.0349 50.6034 51.2## 52.5699 52.5765 54.435C52.6324 54.5139 52.7331 54.5477 52.8256 54.5195C58.6247 52.7249 64.5073 50.0174 70.5802 45.5576C70.6334 45.5182 70.6## 45.459 70.6754 45.3942C72.1 30.0791 68.2 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.937 34.1136 40.937 30.1693C40.937 26.225 43.7636 23.0133 47.3178 23.0133C50.8999 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.8999 37.3253 47.3178 37.3253Z"
    />
  </svg>
);

function LoginDialog({ open, onClose }: LoginDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleGoogleLogin = () => {
    logInWithProvider('google');
    onClose();
  };
  
  const handleAppleLogin = () => {
    logInWithProvider('apple');
    onClose();
  }

  const handleDiscordLogin = () => {
    logInWithProvider('discord');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          m: isMobile ? 2 : 3,
          maxWidth: isMobile ? 'calc(100% - 32px)' : 400,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          variant="h5"
          component="span"
          sx={{ fontWeight: 600 }}
        >
          Sign In
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="close"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          Choose your preferred sign-in method
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Google Sign In Button - Following Google Branding Guidelines */}
          <Button
            variant="outlined"
            fullWidth
            onClick={handleGoogleLogin}
            startIcon={<GoogleIcon />}
            sx={{
              py: 1.5,
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              color: '#1f1f1f',
              backgroundColor: '#ffffff',
              borderColor: '#dadce0',
              justifyContent: 'flex-start',
              '&:hover': {
                backgroundColor: '#f8faff',
                borderColor: '#d2e3fc',
                boxShadow: '0 1px 3px rgba(60,64,67,0.15)',
              },
              '& .MuiButton-startIcon': {
                mr: 2,
              },
            }}
          >
            Continue with Google
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2 }}
            >
              or
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
          
          {/* Apple Sign In Button - Following Apple Branding Guidelines */}
          <Button
            variant="outlined"
            fullWidth
            onClick={handleAppleLogin}
            startIcon={<AppleIcon />}
            sx={{
              py: 1.5,
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              color: '#ffffff',
              backgroundColor: '#000000',
              borderColor: '#dadce0',
              justifyContent: 'flex-start',
              '&:hover': {
                backgroundColor: '#000000',
                borderColor: '#ffffff',
                boxShadow: '0 1px 3px rgba(60,64,67,0.15)',
              },
              '& .MuiButton-startIcon': {
                mr: 2,
              },
            }}
          >
            Continue with Apple
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2 }}
            >
              or
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          {/* Discord Sign In Button - Following Discord Branding Guidelines */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleDiscordLogin}
            startIcon={<DiscordIcon />}
            sx={{
              py: 1.5,
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              color: '#ffffff',
              backgroundColor: '#5865F2', // Discord Blurple
              justifyContent: 'flex-start',
              '&:hover': {
                backgroundColor: '#4752C4',
              },
              '& .MuiButton-startIcon': {
                mr: 2,
              },
            }}
          >
            Continue with Discord
          </Button>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mt: 3,
            textAlign: 'center',
          }}
        >
          By signing in, you agree to our Terms of Service and Privacy Policy
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

export default LoginDialog;


